import { Box, Button, CircularProgress, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import {
  applyResultAtom,
  applyStatusAtom,
  pipelineErrorAtom,
  processResultAtom,
  processStatusAtom,
} from './atoms'
import {
  getApplyResultItems,
  getJobProgressSummary,
  getStatusItems,
} from './formatUtils'
import { getApplyResults, startApply } from './utils'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'

import { FlexBoxHelper } from './FlexBoxHelper'
import { JobProgressBar } from './JobProgressBar'
import { directTreeAccessUrl } from 'infrastructure/api'

export function ApplyStage({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const status = useAtomValue(applyStatusAtom)
  const processStatus = useAtomValue(processStatusAtom)
  const processResult = useAtomValue(processResultAtom)
  const applyResult = useAtomValue(applyResultAtom)
  const pipelineError = useAtomValue(pipelineErrorAtom)
  const setPipelineError = useSetAtom(pipelineErrorAtom)
  const setApplyResult = useSetAtom(applyResultAtom)

  const handleStartStage = async () => {
    try {
      if (processStatus?.status !== 'success') {
        throw new Error('Process results not available')
      }

      await startApply(treeId, versionId, processStatus.controller_id)
      // The PipelineStatus component will pick up the new job status via polling
    } catch (error) {
      setPipelineError(
        `Error starting apply stage: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  // Fetch apply results when status is completed, no error, and result is null
  useEffect(() => {
    const fetchResults = async () => {
      if (!pipelineError && status?.flow_status?.completed && !applyResult) {
        try {
          const results = await getApplyResults(treeId, versionId)
          if (results.data?.results?.[0]) {
            setApplyResult(results.data.results[0])
          }
        } catch (error) {
          setPipelineError(
            `Error fetching apply results: ${error instanceof Error ? error.message : 'Unknown error'}`,
          )
        }
      }
    }

    fetchResults()
  }, [
    pipelineError,
    status?.flow_status?.completed,
    applyResult,
    treeId,
    versionId,
    setApplyResult,
    setPipelineError,
  ])

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Typography variant="h6" gutterBottom sx={{ fontStyle: 'italic' }}>
        Apply
      </Typography>

      {processResult && (
        <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
          <Typography variant="body1">
            This will apply LaTeX conversions to the tree:{' '}
            <a
              href={directTreeAccessUrl({
                treeId: treeId,
                versionId: versionId,
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              {directTreeAccessUrl({
                treeId: treeId,
                versionId: versionId,
              })}
            </a>
          </Typography>
        </Box>
      )}

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartStage}
          disabled={
            pipelineError !== null || processStatus?.status !== 'success'
          }
          size="small"
        >
          Start
        </Button>
      </Box>

      {status && (
        <FlexBoxHelper
          header={
            !status?.flow_status?.completed ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} color="primary" />
                <Typography variant="body2" color="primary">
                  {status.flow_status
                    ? getJobProgressSummary(status.flow_status)
                    : 'Processing...'}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="success.main"
                sx={{ fontWeight: 'bold' }}
              >
                ✓ Completed Successfully
              </Typography>
            )
          }
          items={getStatusItems(status, null)}
        />
      )}

      {status?.flow_status && !status.flow_status.completed && (
        <JobProgressBar flowStatus={status.flow_status} />
      )}

      {applyResult && (
        <FlexBoxHelper
          header={<Typography variant="h6">Apply Results</Typography>}
          items={getApplyResultItems(applyResult)}
        />
      )}
    </Box>
  )
}
